export type StreakLevel = 1 | 3 | 7 | 15 | 30 | 100;

export interface StreakLevelConfig {
  days: StreakLevel;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  gradient: string;
  flameIntensity: 'low' | 'medium' | 'high' | 'extreme' | 'legendary';
  description: string;
  rewardMultiplier: number;
}

export type ActivityType = 'escuchar' | 'comentar' | 'compartir' | 'participar' | 'entrar-cabina';

export interface ActivityConfig {
  key: ActivityType;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
  minMinutes?: number;
  basePoints: number;
  streakBonus: number;
}

export type RewardType = 'puntos' | 'insignia' | 'vip';

export interface RewardConfig {
  key: RewardType;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
  unlockAt: string;
}

export interface StreakUser {
  id: string;
  name: string;
  slug: string;
  photo: string;
  level: number;
  currentStreak: number;
  bestStreak: number;
  totalDays: number;
  currentLevel: StreakLevel;
  bestLevel: StreakLevel;
  activeActivities: ActivityType[];
  todayCompleted: boolean;
  completedAt: string | null;
  lastActivity: string;
  pointsEarned: number;
  badgesUnlocked: string[];
  isVip: boolean;
  rankingPosition: number;
  streakStartedAt: string;
  streakStartedRelative: string;
  riskOfLosing: boolean;
  daysUntilNextLevel: number;
  nextLevel: StreakLevel;
}

export interface StreakHistoryEntry {
  id: string;
  userId: string;
  userName: string;
  activityType: ActivityType;
  date: string;
  dateRelative: string;
  streakBefore: number;
  streakAfter: number;
  pointsEarned: number;
  levelUp: boolean;
  newLevel: StreakLevel | null;
  badgeUnlock: string | null;
  description: string;
}

export interface StreakStats {
  totalActiveStreaks: number;
  totalUsersWithStreaks: number;
  longestActiveStreak: number;
  averageStreak: number;
  streaksAtRisk: number;
  todayCompleted: number;
  levelUpsToday: number;
  totalPointsAwarded: number;
}

export const streakLevels: StreakLevelConfig[] = [
  {
    days: 1,
    label: '1 Día',
    icon: 'ri-fire-line',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    gradient: 'from-amber-400 to-orange-400',
    flameIntensity: 'low',
    description: '¡Primer día! La chispa inicial. El hábito empieza con una sola acción.',
    rewardMultiplier: 1,
  },
  {
    days: 3,
    label: '3 Días',
    icon: 'ri-fire-line',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    gradient: 'from-orange-400 to-red-400',
    flameIntensity: 'medium',
    description: 'Tres días seguidos. El hábito empieza a tomar forma. Vas en serio.',
    rewardMultiplier: 1.2,
  },
  {
    days: 7,
    label: '7 Días',
    icon: 'ri-fire-fill',
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    gradient: 'from-red-400 to-rose-500',
    flameIntensity: 'high',
    description: 'Una semana completa. Ya sos parte de la élite de oyentes consistentes.',
    rewardMultiplier: 1.5,
  },
  {
    days: 15,
    label: '15 Días',
    icon: 'ri-fire-fill',
    color: 'text-rose-500',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    gradient: 'from-rose-400 to-pink-500',
    flameIntensity: 'extreme',
    description: 'Media luna de constancia. Tu compromiso con RFW es evidente.',
    rewardMultiplier: 2,
  },
  {
    days: 30,
    label: '30 Días',
    icon: 'ri-vip-crown-line',
    color: 'text-pink-500',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    gradient: 'from-pink-400 to-fuchsia-500',
    flameIntensity: 'extreme',
    description: 'Un mes entero sin fallar. Digno de reconocimiento especial de la comunidad.',
    rewardMultiplier: 3,
  },
  {
    days: 100,
    label: '100 Días',
    icon: 'ri-vip-crown-fill',
    color: 'text-fuchsia-500',
    bgColor: 'bg-fuchsia-50',
    borderColor: 'border-fuchsia-200',
    gradient: 'from-fuchsia-400 via-purple-400 to-violet-500',
    flameIntensity: 'legendary',
    description: 'Leyenda de la constancia. Tu nombre queda grabado en el Salón de Rachas de RFW.',
    rewardMultiplier: 5,
  },
];

export const activityConfigs: ActivityConfig[] = [
  {
    key: 'escuchar',
    label: 'Escuchar',
    icon: 'ri-headphone-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    description: 'Escuchá al menos 15 minutos de radio en vivo a través de RFW.',
    minMinutes: 15,
    basePoints: 10,
    streakBonus: 2,
  },
  {
    key: 'comentar',
    label: 'Comentar',
    icon: 'ri-chat-1-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    description: 'Dejá al menos un comentario en la cabina, un post, o un evento.',
    basePoints: 5,
    streakBonus: 1,
  },
  {
    key: 'compartir',
    label: 'Compartir',
    icon: 'ri-share-forward-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    description: 'Compartí un programa, evento, concurso o radio con otros oyentes.',
    basePoints: 8,
    streakBonus: 2,
  },
  {
    key: 'participar',
    label: 'Participar',
    icon: 'ri-trophy-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Participá en un concurso, trivia, encuesta o votación activa.',
    basePoints: 15,
    streakBonus: 3,
  },
  {
    key: 'entrar-cabina',
    label: 'Entrar a Cabina',
    icon: 'ri-broadcast-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    description: 'Ingresá a la cabina de cualquier radio y participá de la conversación en vivo.',
    basePoints: 12,
    streakBonus: 3,
  },
];

export const rewardConfigs: RewardConfig[] = [
  {
    key: 'puntos',
    label: 'Puntos RFW',
    icon: 'ri-coin-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    description: 'Puntos extra por cada día de racha. Mientras más larga la racha, más puntos acumulás por actividad. Multiplicador que crece con cada nivel.',
    unlockAt: 'Desde el día 1',
  },
  {
    key: 'insignia',
    label: 'Insignias de Racha',
    icon: 'ri-shield-star-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    description: 'Insignias exclusivas que se desbloquean al alcanzar hitos de racha. Solo se obtienen con constancia real, no se pueden comprar ni regalar.',
    unlockAt: '3, 7, 15, 30 y 100 días',
  },
  {
    key: 'vip',
    label: 'Acceso VIP',
    icon: 'ri-vip-diamond-line',
    color: 'text-fuchsia-600',
    bgColor: 'bg-fuchsia-50',
    borderColor: 'border-fuchsia-300',
    description: 'Los oyentes con rachas legendarias acceden a beneficios VIP: contenido exclusivo, acceso anticipado a eventos y prioridad en concursos.',
    unlockAt: '30 y 100 días de racha',
  },
];

export const streakUsers: StreakUser[] = [
  {
    id: 'str-u-001',
    name: 'María José Herrera',
    slug: 'maria-jose-herrera',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Latina%20woman%20mid%20twenties%20warm%20friendly%20smile%20clean%20studio%20background%20soft%20natural%20lighting%20confident%20approachable%20personality%20editorial%20photography&width=200&height=200&seq=streaks-mariajose&orientation=squarish',
    level: 42,
    currentStreak: 97,
    bestStreak: 124,
    totalDays: 847,
    currentLevel: 30,
    bestLevel: 100,
    activeActivities: ['escuchar', 'comentar', 'compartir', 'participar', 'entrar-cabina'],
    todayCompleted: true,
    completedAt: '2026-06-21T08:15:00Z',
    lastActivity: 'Escuchó La Mega 107.3 FM por 45 minutos',
    pointsEarned: 28400,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días', 'Racha 15 Días', 'Racha 30 Días'],
    isVip: true,
    rankingPosition: 1,
    streakStartedAt: '2026-03-17T07:30:00Z',
    streakStartedRelative: '17 de Marzo 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 3,
    nextLevel: 100,
  },
  {
    id: 'str-u-002',
    name: 'Carlos Andrés Vega',
    slug: 'carlos-andres-vega',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Colombian%20male%20late%20twenties%20focused%20expression%20clean%20studio%20background%20warm%20lighting%20confident%20modern%20editorial%20photography&width=200&height=200&seq=streaks-carlosvega&orientation=squarish',
    level: 38,
    currentStreak: 54,
    bestStreak: 89,
    totalDays: 612,
    currentLevel: 30,
    bestLevel: 30,
    activeActivities: ['escuchar', 'comentar', 'compartir'],
    todayCompleted: true,
    completedAt: '2026-06-21T07:45:00Z',
    lastActivity: 'Compartió RFW Fest 2026',
    pointsEarned: 19800,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días', 'Racha 15 Días', 'Racha 30 Días'],
    isVip: true,
    rankingPosition: 2,
    streakStartedAt: '2026-04-29T06:00:00Z',
    streakStartedRelative: '29 de Abril 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 46,
    nextLevel: 100,
  },
  {
    id: 'str-u-003',
    name: 'Valentina Ruiz',
    slug: 'valentina-ruiz',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Latina%20woman%20early%20twenties%20bright%20natural%20smile%20clean%20studio%20lighting%20warm%20skin%20tones%20editorial%20photography%20modern%20approachable&width=200&height=200&seq=streaks-valentinaruiz&orientation=squarish',
    level: 35,
    currentStreak: 42,
    bestStreak: 56,
    totalDays: 489,
    currentLevel: 30,
    bestLevel: 30,
    activeActivities: ['escuchar', 'participar', 'entrar-cabina'],
    todayCompleted: true,
    completedAt: '2026-06-21T09:30:00Z',
    lastActivity: 'Participó en Trivia RFW Semanal',
    pointsEarned: 16200,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días', 'Racha 15 Días', 'Racha 30 Días'],
    isVip: true,
    rankingPosition: 3,
    streakStartedAt: '2026-05-11T08:00:00Z',
    streakStartedRelative: '11 de Mayo 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 58,
    nextLevel: 100,
  },
  {
    id: 'str-u-004',
    name: 'Diego Fernando López',
    slug: 'diego-fernando-lopez',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20Argentine%20male%20early%20thirties%20relaxed%20confident%20expression%20clean%20studio%20background%20modern%20lighting%20casual%20professional%20editorial%20photography&width=200&height=200&seq=streaks-diegolopez&orientation=squarish',
    level: 28,
    currentStreak: 23,
    bestStreak: 31,
    totalDays: 345,
    currentLevel: 15,
    bestLevel: 30,
    activeActivities: ['escuchar', 'compartir'],
    todayCompleted: false,
    completedAt: null,
    lastActivity: 'Escuchó Rock & Pop 95.1 FM ayer',
    pointsEarned: 9800,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días', 'Racha 15 Días'],
    isVip: false,
    rankingPosition: 4,
    streakStartedAt: '2026-05-30T14:00:00Z',
    streakStartedRelative: '30 de Mayo 2026',
    riskOfLosing: true,
    daysUntilNextLevel: 7,
    nextLevel: 30,
  },
  {
    id: 'str-u-005',
    name: 'Ana Sofía Martínez',
    slug: 'ana-sofia-martinez',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Mexican%20woman%20mid%20twenties%20bright%20genuine%20smile%20studio%20background%20natural%20lighting%20warm%20tones%20editorial%20photography%20modern%20approachable&width=200&height=200&seq=streaks-anasofia&orientation=squarish',
    level: 31,
    currentStreak: 18,
    bestStreak: 45,
    totalDays: 523,
    currentLevel: 15,
    bestLevel: 30,
    activeActivities: ['escuchar', 'comentar', 'participar'],
    todayCompleted: true,
    completedAt: '2026-06-21T10:00:00Z',
    lastActivity: 'Comentó en Carrusel Deportivo',
    pointsEarned: 14200,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días', 'Racha 15 Días'],
    isVip: true,
    rankingPosition: 5,
    streakStartedAt: '2026-06-04T09:15:00Z',
    streakStartedRelative: '4 de Junio 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 12,
    nextLevel: 30,
  },
  {
    id: 'str-u-006',
    name: 'Felipe Torres',
    slug: 'felipe-torres',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Peruvian%20male%20late%20twenties%20casual%20confident%20expression%20clean%20background%20editorial%20lighting%20modern%20approachable%20photography&width=200&height=200&seq=streaks-felipetorres&orientation=squarish',
    level: 22,
    currentStreak: 11,
    bestStreak: 19,
    totalDays: 278,
    currentLevel: 7,
    bestLevel: 15,
    activeActivities: ['escuchar'],
    todayCompleted: true,
    completedAt: '2026-06-21T06:30:00Z',
    lastActivity: 'Escuchó Tropicalísima 102.7 FM',
    pointsEarned: 5400,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días'],
    isVip: false,
    rankingPosition: 6,
    streakStartedAt: '2026-06-11T10:00:00Z',
    streakStartedRelative: '11 de Junio 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 4,
    nextLevel: 15,
  },
  {
    id: 'str-u-007',
    name: 'Camila Restrepo',
    slug: 'camila-restrepo',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Colombian%20woman%20mid%20twenties%20warm%20confident%20smile%20studio%20background%20natural%20lighting%20modern%20editorial%20photography%20approachable%20strong%20presence&width=200&height=200&seq=streaks-camilarestrepo&orientation=squarish',
    level: 19,
    currentStreak: 8,
    bestStreak: 22,
    totalDays: 198,
    currentLevel: 7,
    bestLevel: 15,
    activeActivities: ['escuchar', 'entrar-cabina'],
    todayCompleted: false,
    completedAt: null,
    lastActivity: 'Entró a la cabina de La Mega ayer',
    pointsEarned: 3800,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días'],
    isVip: false,
    rankingPosition: 7,
    streakStartedAt: '2026-06-14T07:00:00Z',
    streakStartedRelative: '14 de Junio 2026',
    riskOfLosing: true,
    daysUntilNextLevel: 7,
    nextLevel: 15,
  },
  {
    id: 'str-u-008',
    name: 'Sebastián Gómez',
    slug: 'sebastian-gomez',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Argentine%20male%20early%20twenties%20relaxed%20natural%20expression%20clean%20studio%20lighting%20editorial%20photography%20modern%20casual%20approachable&width=200&height=200&seq=streaks-sebastiangomez&orientation=squarish',
    level: 14,
    currentStreak: 5,
    bestStreak: 10,
    totalDays: 95,
    currentLevel: 3,
    bestLevel: 7,
    activeActivities: ['compartir', 'participar'],
    todayCompleted: true,
    completedAt: '2026-06-21T11:20:00Z',
    lastActivity: 'Participó en Crea tu Programa',
    pointsEarned: 1500,
    badgesUnlocked: ['Racha 3 Días'],
    isVip: false,
    rankingPosition: 8,
    streakStartedAt: '2026-06-17T15:00:00Z',
    streakStartedRelative: '17 de Junio 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 2,
    nextLevel: 7,
  },
  {
    id: 'str-u-009',
    name: 'Natalia Ochoa',
    slug: 'natalia-ochoa',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Chilean%20woman%20mid%20twenties%20warm%20genuine%20smile%20clean%20background%20studio%20lighting%20natural%20beauty%20editorial%20photography%20modern&width=200&height=200&seq=streaks-nataliaochoa&orientation=squarish',
    level: 16,
    currentStreak: 4,
    bestStreak: 14,
    totalDays: 134,
    currentLevel: 3,
    bestLevel: 7,
    activeActivities: ['escuchar', 'comentar', 'compartir'],
    todayCompleted: true,
    completedAt: '2026-06-21T07:10:00Z',
    lastActivity: 'Escuchó ElectroBeat FM',
    pointsEarned: 2100,
    badgesUnlocked: ['Racha 3 Días'],
    isVip: false,
    rankingPosition: 9,
    streakStartedAt: '2026-06-18T08:30:00Z',
    streakStartedRelative: '18 de Junio 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 3,
    nextLevel: 7,
  },
  {
    id: 'str-u-010',
    name: 'Andrés Felipe Rojas',
    slug: 'andres-felipe-rojas',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Colombian%20male%20late%20twenties%20serious%20focused%20expression%20clean%20studio%20background%20editorial%20lighting%20modern%20strong%20personality&width=200&height=200&seq=streaks-andresrojas&orientation=squarish',
    level: 12,
    currentStreak: 2,
    bestStreak: 8,
    totalDays: 67,
    currentLevel: 1,
    bestLevel: 7,
    activeActivities: ['escuchar'],
    todayCompleted: true,
    completedAt: '2026-06-21T08:45:00Z',
    lastActivity: 'Escuchó Onda Cero 104.5 FM',
    pointsEarned: 800,
    badgesUnlocked: [],
    isVip: false,
    rankingPosition: 10,
    streakStartedAt: '2026-06-20T09:00:00Z',
    streakStartedRelative: '20 de Junio 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 1,
    nextLevel: 3,
  },
  {
    id: 'str-u-011',
    name: 'Lucía Fernández',
    slug: 'lucia-fernandez',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Spanish%20woman%20mid%20twenties%20elegant%20casual%20look%20clean%20studio%20background%20natural%20lighting%20editoral%20photography%20modern%20sophisticated&width=200&height=200&seq=streaks-luciafernandez&orientation=squarish',
    level: 8,
    currentStreak: 1,
    bestStreak: 5,
    totalDays: 34,
    currentLevel: 1,
    bestLevel: 3,
    activeActivities: ['entrar-cabina'],
    todayCompleted: true,
    completedAt: '2026-06-21T12:00:00Z',
    lastActivity: 'Entró a la cabina de Radio Éxito',
    pointsEarned: 200,
    badgesUnlocked: [],
    isVip: false,
    rankingPosition: 11,
    streakStartedAt: '2026-06-21T12:00:00Z',
    streakStartedRelative: 'Hoy',
    riskOfLosing: false,
    daysUntilNextLevel: 2,
    nextLevel: 3,
  },
  {
    id: 'str-u-012',
    name: 'Mateo Hernández',
    slug: 'mateo-hernandez',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Ecuadorian%20male%20early%20twenties%20relaxed%20natural%20smile%20clean%20studio%20background%20editorial%20photography%20modern%20casual%20personality&width=200&height=200&seq=streaks-mateohernandez&orientation=squarish',
    level: 10,
    currentStreak: 6,
    bestStreak: 12,
    totalDays: 89,
    currentLevel: 3,
    bestLevel: 7,
    activeActivities: ['escuchar', 'compartir'],
    todayCompleted: false,
    completedAt: null,
    lastActivity: 'Compartió un evento ayer',
    pointsEarned: 2400,
    badgesUnlocked: ['Racha 3 Días'],
    isVip: false,
    rankingPosition: 12,
    streakStartedAt: '2026-06-16T11:00:00Z',
    streakStartedRelative: '16 de Junio 2026',
    riskOfLosing: true,
    daysUntilNextLevel: 1,
    nextLevel: 7,
  },
  {
    id: 'str-u-013',
    name: 'Isabella Vargas',
    slug: 'isabella-vargas',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Venezuelan%20woman%20early%20twenties%20bright%20engaging%20smile%20clean%20studio%20background%20warm%20lighting%20editorial%20photography%20modern%20vibrant%20personality&width=200&height=200&seq=streaks-isabellavargas&orientation=squarish',
    level: 14,
    currentStreak: 9,
    bestStreak: 16,
    totalDays: 156,
    currentLevel: 7,
    bestLevel: 7,
    activeActivities: ['escuchar', 'comentar', 'participar', 'entrar-cabina'],
    todayCompleted: true,
    completedAt: '2026-06-21T08:00:00Z',
    lastActivity: 'Comentó en Noches de Rock',
    pointsEarned: 3100,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días'],
    isVip: false,
    rankingPosition: 13,
    streakStartedAt: '2026-06-13T06:45:00Z',
    streakStartedRelative: '13 de Junio 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 6,
    nextLevel: 15,
  },
  {
    id: 'str-u-014',
    name: 'Juan Pablo Mejía',
    slug: 'juan-pablo-mejia',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Dominican%20male%20late%20twenties%20charismatic%20smile%20clean%20studio%20background%20editorial%20lighting%20modern%20confident%20approachable%20personality&width=200&height=200&seq=streaks-juanpablo&orientation=squarish',
    level: 20,
    currentStreak: 14,
    bestStreak: 21,
    totalDays: 201,
    currentLevel: 7,
    bestLevel: 15,
    activeActivities: ['escuchar', 'comentar', 'participar'],
    todayCompleted: true,
    completedAt: '2026-06-21T09:50:00Z',
    lastActivity: 'Participó en Batalla de Emisoras',
    pointsEarned: 5600,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días'],
    isVip: false,
    rankingPosition: 14,
    streakStartedAt: '2026-06-08T05:30:00Z',
    streakStartedRelative: '8 de Junio 2026',
    riskOfLosing: false,
    daysUntilNextLevel: 1,
    nextLevel: 15,
  },
  {
    id: 'str-u-015',
    name: 'Sara Milena Duque',
    slug: 'sara-milena-duque',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Colombian%20woman%20late%20twenties%20elegant%20warm%20smile%20clean%20studio%20background%20natural%20lighting%20editorial%20photography%20modern%20sophisticated&width=200&height=200&seq=streaks-saraduque&orientation=squarish',
    level: 25,
    currentStreak: 36,
    bestStreak: 42,
    totalDays: 401,
    currentLevel: 30,
    bestLevel: 30,
    activeActivities: ['escuchar', 'comentar', 'compartir', 'entrar-cabina'],
    todayCompleted: false,
    completedAt: null,
    lastActivity: 'Entró a la cabina de La Kalle ayer',
    pointsEarned: 8700,
    badgesUnlocked: ['Racha 3 Días', 'Racha 7 Días', 'Racha 15 Días', 'Racha 30 Días'],
    isVip: true,
    rankingPosition: 15,
    streakStartedAt: '2026-05-17T10:00:00Z',
    streakStartedRelative: '17 de Mayo 2026',
    riskOfLosing: true,
    daysUntilNextLevel: 64,
    nextLevel: 100,
  },
];

export const streakHistory: StreakHistoryEntry[] = [
  {
    id: 'str-h-001',
    userId: 'str-u-001',
    userName: 'María José Herrera',
    activityType: 'escuchar',
    date: '2026-06-21T08:15:00Z',
    dateRelative: 'Hoy 8:15 AM',
    streakBefore: 96,
    streakAfter: 97,
    pointsEarned: 60,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Escuchó La Mega 107.3 FM durante 45 minutos. Sumó 60 puntos (multiplicador x3 por racha 30+).',
  },
  {
    id: 'str-h-002',
    userId: 'str-u-003',
    userName: 'Valentina Ruiz',
    activityType: 'participar',
    date: '2026-06-21T09:30:00Z',
    dateRelative: 'Hoy 9:30 AM',
    streakBefore: 41,
    streakAfter: 42,
    pointsEarned: 45,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Participó en Trivia RFW Semanal respondiendo 10 preguntas. Sumó 45 puntos (multiplicador x3 por racha 30+).',
  },
  {
    id: 'str-h-003',
    userId: 'str-u-008',
    userName: 'Sebastián Gómez',
    activityType: 'participar',
    date: '2026-06-21T11:20:00Z',
    dateRelative: 'Hoy 11:20 AM',
    streakBefore: 4,
    streakAfter: 5,
    pointsEarned: 18,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Inscribió su idea en Crea tu Programa. Sumó 18 puntos (multiplicador x1.2 por racha 3+).',
  },
  {
    id: 'str-h-004',
    userId: 'str-u-001',
    userName: 'María José Herrera',
    activityType: 'compartir',
    date: '2026-06-20T16:30:00Z',
    dateRelative: 'Ayer 4:30 PM',
    streakBefore: 95,
    streakAfter: 96,
    pointsEarned: 24,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Compartió RFW Fest 2026 con 5 contactos. Sumó 24 puntos (multiplicador x3 por racha 30+).',
  },
  {
    id: 'str-h-005',
    userId: 'str-u-002',
    userName: 'Carlos Andrés Vega',
    activityType: 'escuchar',
    date: '2026-06-20T07:00:00Z',
    dateRelative: 'Ayer 7:00 AM',
    streakBefore: 53,
    streakAfter: 54,
    pointsEarned: 30,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Escuchó Rock & Pop 95.1 FM durante 30 minutos. Sumó 30 puntos (multiplicador x3 por racha 30+).',
  },
  {
    id: 'str-h-006',
    userId: 'str-u-007',
    userName: 'Camila Restrepo',
    activityType: 'entrar-cabina',
    date: '2026-06-20T20:15:00Z',
    dateRelative: 'Ayer 8:15 PM',
    streakBefore: 7,
    streakAfter: 8,
    pointsEarned: 18,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Entró a la cabina de La Mega durante el programa nocturno. Sumó 18 puntos (multiplicador x1.5 por racha 7+).',
  },
  {
    id: 'str-h-007',
    userId: 'str-u-001',
    userName: 'María José Herrera',
    activityType: 'entrar-cabina',
    date: '2026-06-19T19:00:00Z',
    dateRelative: '19 de Junio',
    streakBefore: 94,
    streakAfter: 95,
    pointsEarned: 36,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Estuvo en la cabina de Carrusel Deportivo durante 1 hora. Sumó 36 puntos.',
  },
  {
    id: 'str-h-008',
    userId: 'str-u-002',
    userName: 'Carlos Andrés Vega',
    activityType: 'compartir',
    date: '2026-06-19T14:00:00Z',
    dateRelative: '19 de Junio',
    streakBefore: 52,
    streakAfter: 53,
    pointsEarned: 24,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Compartió Premios RFW 2026 en redes sociales. Sumó 24 puntos.',
  },
  {
    id: 'str-h-009',
    userId: 'str-u-013',
    userName: 'Isabella Vargas',
    activityType: 'escuchar',
    date: '2026-06-18T07:00:00Z',
    dateRelative: '18 de Junio',
    streakBefore: 6,
    streakAfter: 7,
    pointsEarned: 15,
    levelUp: true,
    newLevel: 7,
    badgeUnlock: 'Racha 7 Días',
    description: '¡Alcanzó racha de 7 días! Escuchó Radio Éxito y desbloqueó insignia Racha 7 Días.',
  },
  {
    id: 'str-h-010',
    userId: 'str-u-012',
    userName: 'Mateo Hernández',
    activityType: 'compartir',
    date: '2026-06-18T16:00:00Z',
    dateRelative: '18 de Junio',
    streakBefore: 5,
    streakAfter: 6,
    pointsEarned: 10,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Compartió un evento del Club Fans La Mega. Sumó 10 puntos (multiplicador x1.2 por racha 3+).',
  },
  {
    id: 'str-h-011',
    userId: 'str-u-014',
    userName: 'Juan Pablo Mejía',
    activityType: 'escuchar',
    date: '2026-06-17T06:00:00Z',
    dateRelative: '17 de Junio',
    streakBefore: 13,
    streakAfter: 14,
    pointsEarned: 15,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Escuchó Tropicalísima 102.7 FM por 20 minutos. Sumó 15 puntos (multiplicador x1.5 por racha 7+).',
  },
  {
    id: 'str-h-012',
    userId: 'str-u-004',
    userName: 'Diego Fernando López',
    activityType: 'escuchar',
    date: '2026-06-17T10:00:00Z',
    dateRelative: '17 de Junio',
    streakBefore: 22,
    streakAfter: 23,
    pointsEarned: 30,
    levelUp: false,
    newLevel: null,
    badgeUnlock: null,
    description: 'Escuchó Radio Éxito durante 35 minutos. Sumó 30 puntos (multiplicador x2 por racha 15+).',
  },
];

export const streakStats: StreakStats = {
  totalActiveStreaks: 12480,
  totalUsersWithStreaks: 18950,
  longestActiveStreak: 97,
  averageStreak: 14.2,
  streaksAtRisk: 847,
  todayCompleted: 9124,
  levelUpsToday: 326,
  totalPointsAwarded: 1457000,
};

export const streakRules = [
  {
    id: 1,
    title: 'No perder racha por fallos técnicos',
    description: 'Si la plataforma está caída, si tu internet falla, o si un stream no carga, tu racha está protegida. RFW monitorea la disponibilidad y automáticamente concede "día de gracia" cuando detecta fallos técnicos. Tu hábito no debería romperse por algo fuera de tu control.',
    icon: 'ri-shield-check-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  {
    id: 2,
    title: 'Una actividad alcanza para mantener la racha',
    description: 'No necesitás hacer todo. Con completar al menos una de las cinco actividades diarias (escuchar, comentar, compartir, participar o entrar a cabina), tu racha se mantiene viva. Simple y accesible para todos.',
    icon: 'ri-check-double-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
  },
  {
    id: 3,
    title: 'Las rachas se reinician a cero al fallar',
    description: 'Si pasás un día completo sin realizar ninguna actividad — y no hubo fallos técnicos — tu racha vuelve a empezar. Pero tus puntos acumulados, insignias desbloqueadas y beneficios VIP no se pierden. Lo que construiste, queda.',
    icon: 'ri-restart-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
  },
];

export function getStreakLevelConfig(days: StreakLevel): StreakLevelConfig {
  return streakLevels.find((l) => l.days === days) ?? streakLevels[0];
}

export function getCurrentLevelConfig(currentStreak: number): StreakLevelConfig {
  const levels = [...streakLevels].reverse();
  for (const level of levels) {
    if (currentStreak >= level.days) return level;
  }
  return streakLevels[0];
}

export function getNextLevel(currentStreak: number): StreakLevelConfig | null {
  for (const level of streakLevels) {
    if (level.days > currentStreak) return level;
  }
  return null;
}

export function getDaysUntilNextLevel(currentStreak: number): number {
  const next = getNextLevel(currentStreak);
  if (!next) return 0;
  return next.days - currentStreak;
}

export function getUsersByLevel(level: StreakLevel): StreakUser[] {
  return streakUsers.filter((u) => u.currentLevel === level);
}

export function getUsersByActivity(activity: ActivityType | 'todas'): StreakUser[] {
  if (activity === 'todas') return streakUsers;
  return streakUsers.filter((u) => u.activeActivities.includes(activity));
}

export function getUsersAtRisk(): StreakUser[] {
  return streakUsers.filter((u) => u.riskOfLosing);
}

export function getHistoryByUser(userId: string): StreakHistoryEntry[] {
  return streakHistory.filter((h) => h.userId === userId);
}

export function getTopStreaks(limit: number = 10): StreakUser[] {
  return [...streakUsers].sort((a, b) => b.currentStreak - a.currentStreak).slice(0, limit);
}

export function getRecentHistory(limit: number = 10): StreakHistoryEntry[] {
  return [...streakHistory].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, limit);
}

export function getLevelDistribution(): { level: StreakLevel; count: number }[] {
  return streakLevels.map((l) => ({
    level: l.days,
    count: streakUsers.filter((u) => u.currentLevel === l.days).length,
  }));
}

export function getActivityDistribution(): { activity: ActivityType; count: number }[] {
  return activityConfigs.map((a) => ({
    activity: a.key,
    count: streakUsers.filter((u) => u.activeActivities.includes(a.key)).length,
  }));
}